import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../../utils/getUser";
import { base64ToFile } from "../../utils/util";
import { cn } from "../../lib/utils";
import { analyticsMember } from "../../reducers/analytics.slice";
import { TIER_CONFIG, TIER_ORDER, getTierKey, stringToColor } from "./tierConfig";

// This sidebar widget mounts once per session (MemberLayout renders it
// outside the route <Outlet>, so page navigation doesn't remount it) — one
// analytics fetch on mount, not one per page.
export default function UserProfileCard({ collapsed = false, onNavigate }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = getUsers();
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    dispatch(analyticsMember({ userId: user.id })).then((res) => {
      if (res?.payload) setEarnings(res.payload.earnings_overview || 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user) return null;

  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`;
  const tierKey = getTierKey(user);
  const tier = TIER_CONFIG[tierKey] || TIER_CONFIG.member;
  const avatarBg = stringToColor(name || "User");

  // Same progress-to-next-tier math as the dashboard's TierProgressBar
  // (DashboardWidgets.jsx) — reused here rather than reinvented so the two
  // never disagree. Ring stays at a neutral partial fill until the earnings
  // fetch resolves, then animates to the real value.
  const tierIndex = TIER_ORDER.indexOf(tierKey);
  const nextTier = TIER_ORDER[tierIndex + 1] ? TIER_CONFIG[TIER_ORDER[tierIndex + 1]] : null;
  const prevThreshold = tierIndex > 0 ? TIER_CONFIG[TIER_ORDER[tierIndex - 1]].thresholdValue : 0;
  const progressPct =
    earnings == null
      ? 0
      : nextTier
        ? Math.min(100, Math.max(0, ((earnings - prevThreshold) / (nextTier.thresholdValue - prevThreshold)) * 100))
        : 100;

  let avatarSrc = null;
  if (user.profile_picture) {
    try {
      avatarSrc = URL.createObjectURL(
        base64ToFile(user.profile_picture, "avatar", "image/*")
      );
    } catch {
      avatarSrc = null;
    }
  }

  return (
    <div
      className="tw-p-3 tw-cursor-pointer hover:tw-bg-white/[0.03] tw-transition-colors"
      style={{ padding: 12, cursor: "pointer" }}
      onClick={() => {
        navigate("/dashboard/my-profile");
        onNavigate?.();
      }}
      title={collapsed ? name : undefined}
    >
      <div
        className="tw-flex tw-items-center"
        style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start" }}
      >
        {/* Avatar with progress ring */}
        <div
          className="tw-relative tw-w-9 tw-h-9 tw-shrink-0"
          style={{ position: "relative", width: 36, height: 36, marginRight: collapsed ? 0 : 10, flexShrink: 0, transition: "margin-right 0.2s ease" }}
        >
          <svg
            className="tw-absolute tw-inset-0 tw-w-full tw-h-full"
            viewBox="0 0 36 36"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="2.5"
            />
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              stroke={tier.color}
              strokeWidth="2.5"
              strokeDasharray={`${progressPct * 1.005} ${100.5 - progressPct * 1.005}`}
              strokeLinecap="round"
              transform="rotate(-90 18 18)"
              style={{ transition: "stroke-dasharray 0.6s ease" }}
            />
          </svg>
          <div
            className="tw-absolute tw-inset-[3px] tw-rounded-full tw-overflow-hidden tw-flex tw-items-center tw-justify-center"
            style={{ position: "absolute", inset: 3, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={name}
                className="tw-w-full tw-h-full tw-object-cover tw-rounded-full"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
              />
            ) : (
              <div
                className="tw-w-full tw-h-full tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-bold tw-text-white"
                style={{ width: "100%", height: "100%", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", backgroundColor: avatarBg }}
              >
                {initials}
              </div>
            )}
          </div>
        </div>

        {/* Name and tier */}
        <div
          className="tw-min-w-0"
          style={{
            minWidth: 0,
            flex: collapsed ? "0 0 0px" : "1 1 auto",
            maxWidth: collapsed ? 0 : 220,
            opacity: collapsed ? 0 : 1,
            overflow: "hidden",
            transition: "opacity 0.16s ease, max-width 0.22s ease",
          }}
        >
          <div
            className="tw-text-[12px] tw-font-bold tw-text-white tw-truncate"
            style={{ fontSize: 12, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {name}
          </div>
          <div
            className="tw-text-[10px] tw-font-extrabold tw-tracking-[0.05em]"
            style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", color: tier.color, whiteSpace: "nowrap" }}
          >
            {tier.emoji} {tier.label}
          </div>
        </div>
      </div>
    </div>
  );
}
